import { defineMessages } from "react-intl"
import { ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { PREFERENCE_TYPE } from "../../constants"

export enum PriceListColumnIds {
    IDENTIFIER = "identifier",
    LOCATION_NAME = "locationName",
    TITLE = "title",
    ACTIVATED_AT_DATE = "activatedAt",
    STATUS = "status",
    NUMBER_OF_ORDERS = "numberOfOrders",
    TOTAL_AMOUNT = "amountOfOrders",
    LAST_ORDER_AT = "lastOrderAt",
    SHARE_TITLE = "shareTitleKeyword",
    SHARE_DESCRIPTION = "shareDescriptionKeyword",
    DEACTIVATED_AT_DATE = "deactivatedAt",
    CREATED_AT_DATE = "createdAt",
    UPDATED_AT_DATE = "updatedAt",
    PRODUCTS_TITLE = "productsTitle",
    PRODUCTS_SKU = "productsSku",
    PRODUCTS_LABELS = "productsLabels",
    CURRENCY = "currency",
    TAX_ID = "taxId",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: PriceListColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/price-lists/{id}/edit",
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PriceListColumnIds.LOCATION_NAME,
        sortId: "locationNameSort",
        dataType: DataType.LOCATION,
        dataAttributes: {
            redirect: "/locations/{locationId}/edit"
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PriceListColumnIds.TITLE,
        sortId: "titleSort",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PriceListColumnIds.ACTIVATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: PriceListColumnIds.STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                "draft": {
                    message: {
                        id: "generic-listing.price-lists.badge.draft",
                        defaultMessage: "Draft",
                    },
                    type: "primary",
                },
                "active": {
                    message: {
                        id: "generic-listing.price-lists.badge.active",
                        defaultMessage: "Active",
                    },
                    type: "success",
                },
                "inactive": {
                    message: {
                        id: "generic-listing.price-lists.badge.inactive",
                        defaultMessage: "Inactive",
                    },
                    type: "secondary",
                },
                "disabled": {
                    message: {
                        id: "generic-listing.price-lists.badge.disabled",
                        defaultMessage: "Not Available",
                    },
                    type: "secondary",
                },
                "completed": {
                    message: {
                        id: "generic-listing.price-lists.badge.completed",
                        defaultMessage: "Completed",
                    },
                    type: "secondary",
                },
            },
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PriceListColumnIds.NUMBER_OF_ORDERS,
        dataType: DataType.STRING,
        dataAttributes: {
            textAlign: "right",
            defaultValue: "0",
        },
        totalAttributes: {
            key: "totals.numberOfOrders",
            type: TotalType.STRING,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PriceListColumnIds.TOTAL_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.amountOfOrders",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: PriceListColumnIds.LAST_ORDER_AT,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: PriceListColumnIds.SHARE_TITLE,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: PriceListColumnIds.SHARE_DESCRIPTION,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: PriceListColumnIds.DEACTIVATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: PriceListColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: PriceListColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: PriceListColumnIds.PRODUCTS_TITLE,
        accessorKey: "rows",
        sortId: "rowsObject.title",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "title",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: PriceListColumnIds.PRODUCTS_SKU,
        accessorKey: "rows",
        sortId: "rowsObject.sku",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "sku",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: PriceListColumnIds.PRODUCTS_LABELS,
        accessorKey: "rows",
        sortId: "rowsObject.labelIds",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "labelIds",
            optionsKey: "labelOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: PriceListColumnIds.CURRENCY,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: PriceListColumnIds.TAX_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
]

const headerMessages = defineMessages({
    [PriceListColumnIds.IDENTIFIER]: {
        id: "generic-listing.price-lists.table-header.identifier",
        defaultMessage: "ID",
    },
    [PriceListColumnIds.LOCATION_NAME]: {
        id: "generic-listing.price-lists.table-header.location-name",
        defaultMessage: "Location",
    },
    [PriceListColumnIds.TITLE]: {
        id: "generic-listing.price-lists.table-header.title",
        defaultMessage: "Title",
    },
    [PriceListColumnIds.ACTIVATED_AT_DATE]: {
        id: "generic-listing.price-lists.table-header.activated-at-date",
        defaultMessage: "Activated At",
    },
    [PriceListColumnIds.STATUS]: {
        id: "generic-listing.price-lists.table-header.status",
        defaultMessage: "Status",
    },
    [PriceListColumnIds.NUMBER_OF_ORDERS]: {
        id: "generic-listing.price-lists.table-header.number-of-orders",
        defaultMessage: "Number of Orders",
    },
    [PriceListColumnIds.TOTAL_AMOUNT]: {
        id: "generic-listing.price-lists.table-header.total-amount",
        defaultMessage: "Total Amount",
    },
    [PriceListColumnIds.LAST_ORDER_AT]: {
        id: "generic-listing.price-lists.table-header.last-order-at-date",
        defaultMessage: "Last Order At",
    },
    [PriceListColumnIds.SHARE_TITLE]: {
        id: "generic-listing.price-lists.table-header.share-title",
        defaultMessage: "Share Title",
    },
    [PriceListColumnIds.SHARE_DESCRIPTION]: {
        id: "generic-listing.price-lists.table-header.share-description",
        defaultMessage: "Share Description",
    },
    [PriceListColumnIds.DEACTIVATED_AT_DATE]: {
        id: "generic-listing.price-lists.table-header.deactivated-at-date",
        defaultMessage: "Deactivated At",
    },
    [PriceListColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.price-lists.table-header.created-at-date",
        defaultMessage: "Created At",
    },
    [PriceListColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.price-lists.table-header.update-at-date",
        defaultMessage: "Updated At",
    },
    [PriceListColumnIds.PRODUCTS_TITLE]: {
        id: "generic-listing.price-lists.table-header.product-title",
        defaultMessage: "Products Title",
    },
    [PriceListColumnIds.PRODUCTS_SKU]: {
        id: "generic-listing.price-lists.table-header.product-sku",
        defaultMessage: "Products SKU",
    },
    [PriceListColumnIds.PRODUCTS_LABELS]: {
        id: "generic-listing.price-lists.table-header.product-labels",
        defaultMessage: "Products Labels",
    },
    [PriceListColumnIds.CURRENCY]: {
        id: "generic-listing.price-lists.table-header.currency",
        defaultMessage: "Currency",
    },
    [PriceListColumnIds.TAX_ID]: {
        id: "generic-listing.price-lists.table-header.tax-id",
        defaultMessage: "Tax",
    },
})

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.PRICE_LISTS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_PRICE_LISTS,
    searchPlaceholder: {
        id: "generic-listing.price-lists.search-placeholder",
        defaultMessage: "Search order forms...",
    },
    displayString: {
        id: "generic-listing.price-lists.display-string",
        defaultMessage: "Order Forms",
    },
    searchObjectType: ObjectType.PRICE_LISTS,
    typeOfSearch: "priceList",
    bulkActionDefinitions: [],
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/PriceListCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/price-lists.ts